import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Overlay, Tooltip } from "react-bootstrap"
import styled from "styled-components"

import {
    IDLE, PENDING,
    EDIT,
    NO_SELECTION, PRESENT, PARTIAL,
    TABLE_EDITABLE_CELL,
} from "const"
import { MtStudySessionAttendance } from "server/models"
import { ContentColumn, ContentWrapper } from "client/parts/layout"
import { StateType, StudySessionAttendanceType, hasProjectAccess } from "client/utils"
import {
    PRM_STUDY_SESSION,
    StoreType,
    LoadAllStudySessionAttendance,
    SaveStudySessionAttendance,
    setStudySessionAttendance,
} from "client/store"
import {
    Container,
    PageContent,
    PageForm,
    Label,
    Loader,
    Collapse,
    TableWrapper,
    TableScroller,
    TableContentWrapper,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableCellContentWrapper,
    DateFormatter,
} from "client/components"

const TableContentWrapperStyled = styled(TableContentWrapper)`
    td, th {
        border: 1px solid ${(props) => props.theme.tableRowBorderColor};
        &::after {
            display: none;
        }
    }
`
const TableCellEditable = styled(TableCell)`
    ${(props) => props.isEditable && `
        border: 1px solid ${props.theme.tableRowBorderColor} !important;
        background: ${props.theme.tableRowSelectedBackground};
    `}
`

const TooltipStyled = styled(Tooltip)`
    opacity: 1 !important;

    & > .tooltip-inner {
        min-width: 200px;
        max-width: unset;
        padding: 0;
        border-radius: 0px;
        background: ${(props) => props.theme.selectDropdownBackground};
        font-family: ${(props) => props.theme.defaultFont};
        font-size: 14px;

        & > div {
            width: 100%;
            padding: 6px 12px;
            text-align: left;
            color: ${(props) => props.theme.selectDropdownItemColor};
            background: transparent;
            transition: ${(props) => props.theme.defaultTransition};

            &:hover {
                color: ${(props) => props.theme.selectDropdownItemHoverColor};
                background: ${(props) => props.theme.selectDropdownHoverBackground};
            }
        }
    }

    & > .tooltip-arrow {
        display: none;
    }
`

type ActiveCell = {
    sessionId: number
    teamMemberId: number
}

const rowSize = "sm"

const SessionAttendance = () => {
    const studyList = useSelector((state: StoreType) => state.study.studyList)
    const studyIds = useSelector((state: StoreType) => state.study.studyIds)
    const sessionList = useSelector((state: StoreType) => state.studySession.sessionList)
    const sessionListByStudy = useSelector((state: StoreType) => state.studySession.sessionListByStudy)
    const sessionAttendance = useSelector((state: StoreType) => state.studySession.sessionAttendance)
    const teamMemberList = useSelector((state: StoreType) => state.studyTeamMember.teamMemberList)
    const teamMemberListByStudy = useSelector((state: StoreType) => state.studyTeamMember.teamMemberListByStudy)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [activeCell, setActiveCell] = useState<ActiveCell>(null)
    const [target, setTarget] = useState<HTMLElement>(null)
    const [show, setShow] = useState(false)
    const [saveState, setSaveState] = useState<StateType>(IDLE)

    const clearActiveCell = () => {
        setActiveCell(null)
        setShow(false)
    }

    useEffect(() => {
        document.addEventListener("click", clearActiveCell)
        return () => document.removeEventListener("click", clearActiveCell)
    }, [])

    useEffect(() => {
        if (show && target) {
            // !!!IMPORTANT!!! height of the document and height of the ContentColumn returns wrong value!!!
            const scrollableParent = document.body.querySelector(ContentColumn) as HTMLElement
            const content = document.body.querySelector(ContentWrapper) as HTMLElement
            const documentHeight = content.offsetHeight + 63 * 2 // where 63 - height for header and footer

            const targetBounding = target.getBoundingClientRect()
            const tooltip = document.getElementById("sessionAttendance").getElementsByClassName("tooltip-inner")[0] as HTMLElement
            const diff = documentHeight - (scrollableParent.scrollTop + targetBounding.top + targetBounding.height)

            tooltip.style.width = `${Math.floor(targetBounding.width)}px`
            if (diff > tooltip.offsetHeight) {
                tooltip.style.transform = "translateY(-6px)"
            } else {
                tooltip.style.transform = `translateY(-${targetBounding.height + tooltip.offsetHeight + 5}px)`
            }
        }
    }, [show, target])

    const onChange = (attendance: StudySessionAttendanceType) => {
        const data: MtStudySessionAttendance = {
            teamMemberId: activeCell.teamMemberId,
            sessionId: activeCell.sessionId,
            projectId: project.id,
            attendance,
        }
        setActiveCell(null)
        setShow(false)
        setSaveState(PENDING)
        SaveStudySessionAttendance([data]).then(() => (
            LoadAllStudySessionAttendance(project.id).then((res) => {
                dispatch(setStudySessionAttendance(res))
                setSaveState(IDLE)
            })
        ))
    }

    const editAccess = hasProjectAccess(PRM_STUDY_SESSION, EDIT)

    return (
        <Container>
            <PageContent>
                {
                    saveState === PENDING
                    && <Loader />
                }
                <PageForm style={{ paddingTop: "35px" }}>
                    <Label>{lang.attendance}</Label>
                </PageForm>
                {
                    studyIds.map((studyId) => {
                        const sessionIds = sessionListByStudy[studyId]
                        const teamMemberIds = teamMemberListByStudy[studyId]
                        if (!sessionIds || !sessionIds.length) return null

                        return (
                            <Collapse
                                key={studyId}
                                show
                                title={studyList[studyId] && studyList[studyId].name}
                            >
                                <TableWrapper>
                                    <TableScroller>
                                        <TableContentWrapperStyled>
                                            <thead>
                                                <TableRow noHover size={rowSize}>
                                                    <TableHeaderCell />
                                                    <TableHeaderCell colSpan={sessionIds.length}>{lang.sessions}</TableHeaderCell>
                                                </TableRow>
                                                <TableRow noHover size={rowSize}>
                                                    <TableHeaderCell>{lang.teamMembers}</TableHeaderCell>
                                                    {
                                                        sessionIds.map((sessionId) => {
                                                            const session = sessionList[sessionId]
                                                            if (!session) return null

                                                            return (
                                                                <TableHeaderCell key={sessionId}>
                                                                    <DateFormatter date={session.date} showTime={false} />
                                                                </TableHeaderCell>
                                                            )
                                                        })
                                                    }
                                                </TableRow>
                                            </thead>
                                            <tbody>
                                                {
                                                    teamMemberIds && teamMemberIds.map((teamMemberId) => {
                                                        const teamMember = teamMemberList[teamMemberId]
                                                        if (!teamMember) return null

                                                        return (
                                                            <TableRow key={teamMemberId} noHover size={rowSize}>
                                                                <TableCell>
                                                                    <TableCellContentWrapper>{teamMember.name}</TableCellContentWrapper>
                                                                </TableCell>
                                                                {
                                                                    sessionIds.map((sessionId) => {
                                                                        const session = sessionList[sessionId]
                                                                        if (!session) return null

                                                                        const attendance = (
                                                                            sessionAttendance[sessionId]
                                                                            && sessionAttendance[sessionId][teamMemberId]
                                                                        ) || NO_SELECTION

                                                                        let attendanceText: string
                                                                        if (attendance === NO_SELECTION) attendanceText = lang.noSelection
                                                                        if (attendance === PRESENT) attendanceText = lang.present
                                                                        if (attendance === PARTIAL) attendanceText = lang.partial

                                                                        const isEditable = activeCell
                                                                            && activeCell.sessionId === sessionId
                                                                            && activeCell.teamMemberId === teamMemberId

                                                                        return (
                                                                            <TableCellEditable
                                                                                key={sessionId}
                                                                                isEditable={isEditable}
                                                                                className={TABLE_EDITABLE_CELL}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation()
                                                                                    if (!editAccess) return

                                                                                    setActiveCell({
                                                                                        sessionId,
                                                                                        teamMemberId,
                                                                                    })
                                                                                    setTarget(e.currentTarget)
                                                                                    setShow(true)
                                                                                }}
                                                                            >
                                                                                <TableCellContentWrapper>
                                                                                    {attendanceText}
                                                                                </TableCellContentWrapper>
                                                                            </TableCellEditable>
                                                                        )
                                                                    })
                                                                }
                                                            </TableRow>
                                                        )
                                                    })
                                                }
                                            </tbody>
                                        </TableContentWrapperStyled>
                                    </TableScroller>
                                </TableWrapper>
                            </Collapse>
                        )
                    })
                }

                <Overlay target={target} show={show} placement="bottom-start">
                    <TooltipStyled id="sessionAttendance">
                        <div onClick={() => onChange(NO_SELECTION)}>{lang.noSelection}</div>
                        <div onClick={() => onChange(PRESENT)}>{lang.present}</div>
                        <div onClick={() => onChange(PARTIAL)}>{lang.partial}</div>
                    </TooltipStyled>
                </Overlay>
            </PageContent>
        </Container>
    )
}
export default SessionAttendance
